import type {
  AvailabilityOverrideCreateRequest,
  AvailabilityOverrideResponse,
  AvailabilityPreviewParams,
  AvailabilityPreviewResponse,
} from '../types';
import type { RequestFn } from '../client';

/**
 * Merchant payment-method availability overrides. Force-show or force-hide a
 * payment method per country/region at global, project, or shop scope, on top
 * of the curated country defaults. Admin (`sk_*`) access required.
 */
export class AvailabilityOverrides {
  constructor(private readonly request: RequestFn) {}

  /** Create an availability override. `POST /availability-overrides` */
  async create(params: AvailabilityOverrideCreateRequest): Promise<AvailabilityOverrideResponse> {
    return this.request('POST', '/availability-overrides', { body: params });
  }

  /** List a merchant's availability overrides. `GET /availability-overrides` */
  async list(merchantId: string): Promise<AvailabilityOverrideResponse[]> {
    return this.request('GET', '/availability-overrides', {
      query: { merchant_id: merchantId },
    });
  }

  /** Delete an availability override by id. `DELETE /availability-overrides/{id}` */
  async delete(id: string): Promise<boolean> {
    return this.request('DELETE', `/availability-overrides/${encodeURIComponent(id)}`);
  }

  /**
   * Preview the methods a customer in `country` would be shown for a shop —
   * the connector ceiling narrowed by the smart country defaults and the
   * merchant overrides, without an active payment. Pass `amount` + `currency`
   * to also evaluate order-value rules.
   * `GET /availability-overrides/preview`
   */
  async preview(params: AvailabilityPreviewParams): Promise<AvailabilityPreviewResponse> {
    return this.request('GET', '/availability-overrides/preview', {
      query: {
        merchant_id: params.merchant_id,
        profile_id: params.profile_id,
        ...(params.country ? { country: params.country } : {}),
        // `0` is a legitimate order value, so test for presence, not truthiness.
        ...(params.amount !== undefined ? { amount: params.amount } : {}),
        ...(params.currency ? { currency: params.currency } : {}),
      },
    });
  }
}
